import { useMemo , useState} from 'react';
import { NFLTeamRosterOverlay, type OverlayTeam } from './NFLTeamRosterOverlay';
import NFLPlayerDetailModal from './NFLPlayerDetailModal';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import TeamLogo from './TeamLogo';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, Shield, Crown, Skull, Bomb, Flame, Zap, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { BonusesPanel } from './bonuses/BonusesPanel';
import { SurvivorPanel } from './bonuses/SurvivorPanel';

interface AwardData {
  id: string;
  icon: React.ReactNode;
  label: string;
  teamName: string;
  value: string;
  color: string;
  description: string;
}

interface NFLStandingsPageProps {
  /**
   * SAB-1046: opens the existing trade flow pre-targeted at a team. Optional, because
   * standings also renders in contexts with no trade surface to reach -- the overlay's
   * Trade button is withheld rather than dead when it is absent, same rule as the own-team
   * case.
   */
  onTradeWith?: (teamId: string, playerId?: string) => void;
}

export default function NFLStandingsPage({ onTradeWith }: NFLStandingsPageProps = {}) {
  // SAB-1068: which player the overlay asked to open. Held here, not in the overlay,
  // because the overlay closes before the modal opens and would unmount its own state.
  const [detailPlayerId, setDetailPlayerId] = useState<string | null>(null);
  const { currentLeague, currentTeam, teams, isLoading: contextLoading } = useNFLLeague();
  // SAB-1046: which team's roster is open. Null = closed.
  const [overlayTeam, setOverlayTeam] = useState<OverlayTeam | null>(null);

  const playoffTeams = (currentLeague as any)?.playoff_teams ?? 4;

  const sorted = useMemo(() => {
    return [...teams].sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      return b.points_for - a.points_for;
    });
  }, [teams]);

  // Compute games back
  const leaderWins = sorted[0]?.wins || 0;
  const leaderLosses = sorted[0]?.losses || 0;

  if (contextLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentLeague) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Trophy className="h-10 w-10 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">Join a league to see standings</p>
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <div className="space-y-4">
        <h1 className="text-lg font-display font-bold text-foreground">Standings</h1>
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="h-12 w-12 rounded-full bg-amber-400/10 flex items-center justify-center mb-4">
              <Trophy className="h-6 w-6 text-amber-400/50" />
            </div>
            <p className="text-sm text-muted-foreground font-medium">No teams yet</p>
            <p className="text-xs text-muted-foreground mt-1">Standings will appear once teams join the league</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold font-display text-foreground flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary" />
          Standings
        </h1>
        <Badge variant="outline" className="text-xs">{sorted.length} teams</Badge>
      </div>

      <Tabs defaultValue="standings" className="space-y-3">
        <TabsList>
          <TabsTrigger value="standings">Standings</TabsTrigger>
          <TabsTrigger value="bonuses">Bonuses</TabsTrigger>
          <TabsTrigger value="survivor">Survivor</TabsTrigger>
        </TabsList>

        <TabsContent value="standings" className="space-y-3 mt-0">
      {/* Horizontal-scroll wrapper. The # and Team cells are sticky-left so
          stat columns can scroll under them on mobile without losing the
          team identity. min-width on the table forces all columns to render
          at full width on small screens (no responsive hiding). */}
      <Card className="bg-card border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-muted/50 text-muted-foreground text-xs">
                <th className="text-left px-2 sm:px-3 py-2.5 w-10 sticky left-0 bg-muted/50 z-10">#</th>
                <th className="text-left px-2 sm:px-3 py-2.5 min-w-[140px] sticky left-10 bg-muted/50 z-10">Team</th>
                <th className="text-center px-2 py-2.5 w-10">W</th>
                <th className="text-center px-2 py-2.5 w-10">L</th>
                <th className="text-center px-2 py-2.5 w-14">PCT</th>
                <th className="text-center px-2 py-2.5 w-12">GB</th>
                <th className="text-center px-2 py-2.5 w-16">Streak</th>
                <th className="text-right px-2 sm:px-3 py-2.5 w-14">PF</th>
                <th className="text-right px-2 py-2.5 w-14">PA</th>
                <th className="text-right px-2 py-2.5 w-14">Diff</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((team, i) => {
                const rank = i + 1;
                const isMe = team.id === currentTeam?.id;
                const totalGames = team.wins + team.losses + (team.ties || 0);
                const winPct = totalGames > 0 ? (team.wins / totalGames) : 0;
                const diff = team.points_for - team.points_against;
                const isPlayoffSpot = rank <= playoffTeams;
                const isPlayoffBorder = rank === playoffTeams;
                const gb = ((leaderWins - team.wins) + (team.losses - leaderLosses)) / 2;
                // The sticky cells inherit the row background so the scroll
                // doesn't reveal a stripe. Uses bg-card (rest) or primary tint
                // when the row is the current user's team.
                const stickyBg = isMe ? 'bg-[#1c1917]' : 'bg-card';

                return (
                  <tr
                    key={team.id}
                    data-testid="standings-row"
                    // SAB-1046: the row opens the team's roster. Every row, including your
                    // own -- only the Trade button inside is withheld there.
                    role="button"
                    tabIndex={0}
                    onClick={() => setOverlayTeam(team as unknown as OverlayTeam)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOverlayTeam(team as unknown as OverlayTeam);
                      }
                    }}
                    className={cn(
                      'border-t border-border/50 transition-colors cursor-pointer hover:bg-muted/40',
                      isMe && 'bg-primary/5',
                      isPlayoffBorder && 'border-b-2 border-b-primary/30',
                    )}
                  >
                    <td className={cn('px-2 sm:px-3 py-3 sticky left-0 z-10', stickyBg)}>
                      <div className="flex items-center gap-1">
                        <span className={cn(
                          'font-bold',
                          rank <= 3 ? 'text-amber-400' : 'text-muted-foreground'
                        )}>{rank}</span>
                        {isPlayoffSpot && (
                          <Shield className="w-3 h-3 text-primary shrink-0" />
                        )}
                      </div>
                    </td>
                    <td className={cn('px-2 sm:px-3 py-3 min-w-[140px] max-w-[180px] sticky left-10 z-10', stickyBg)}>
                      <div className="flex items-center gap-1.5 min-w-0">
                        {/* SAB-1004: standings had no team avatar at all. The cell is sticky
                            and narrow, so this is deliberately small and never wraps. */}
                        <TeamLogo
                          logoUrl={team.logo_url}
                          name={team.name}
                          className="h-5 w-5"
                          textClassName="text-[9px]"
                        />
                        <span
                          className={cn('font-medium truncate min-w-0', isMe ? 'text-primary' : 'text-foreground')}
                          title={team.name}
                        >
                          {team.name}
                        </span>
                        {isMe && (
                          <Badge variant="secondary" className="text-[9px] px-1 h-4 shrink-0">YOU</Badge>
                        )}
                      </div>
                    </td>
                    <td className="text-center px-2 py-3 font-semibold text-foreground">{team.wins}</td>
                    <td className="text-center px-2 py-3 font-semibold text-muted-foreground">{team.losses}</td>
                    <td className="text-center px-2 py-3 text-muted-foreground tabular-nums">
                      {winPct.toFixed(3).replace(/^0/, '')}
                    </td>
                    <td className="text-center px-2 py-3 text-muted-foreground tabular-nums">
                      {i === 0 ? '-' : gb.toFixed(1)}
                    </td>
                    <td className="text-center px-2 py-3">
                      {(team as any).streak ? (
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[10px] px-1.5',
                            (team as any).streak?.startsWith('W') && 'border-primary/40 text-primary',
                            (team as any).streak?.startsWith('L') && 'border-destructive/40 text-destructive',
                          )}
                        >
                          {(team as any).streak}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="text-right px-2 sm:px-3 py-3 font-bold text-primary tabular-nums">
                      {Math.round(team.points_for)}
                    </td>
                    <td className="text-right px-2 py-3 text-muted-foreground tabular-nums">
                      {Math.round(team.points_against)}
                    </td>
                    <td className={cn(
                      'text-right px-2 py-3 font-medium tabular-nums',
                      diff > 0 ? 'text-primary' : diff < 0 ? 'text-destructive' : 'text-muted-foreground'
                    )}>
                      {diff > 0 ? '+' : ''}{diff.toFixed(1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Playoff cutline legend */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Shield className="w-3 h-3 text-primary" />
        <span>Top {playoffTeams} teams make the playoffs</span>
      </div>

      <NFLTeamRosterOverlay
        team={overlayTeam}
        open={!!overlayTeam}
        onOpenChange={(v) => { if (!v) setOverlayTeam(null); }}
        leagueId={currentLeague?.id ?? null}
        rosterSlots={currentLeague?.settings?.roster_slots}
        week={currentLeague?.current_week ?? null}
        isOwnTeam={overlayTeam?.id === currentTeam?.id}
        onTrade={onTradeWith}
        onPlayerClick={(pid) => setDetailPlayerId(pid)}
      />

      {/* SAB-1068: the fourth entry point into the detail modal. Wired to the SAME
          onTradeWith the other three use, so the Trade button inside it pre-targets the
          owning team exactly as it does from Matchups, Players and My Team -- rather than
          this surface growing a second opinion about what Trade means. */}
      <NFLPlayerDetailModal
        onTrade={onTradeWith ? (teamId, pid) => onTradeWith(teamId, pid) : undefined}
        playerId={detailPlayerId}
        open={!!detailPlayerId}
        onOpenChange={(open) => { if (!open) setDetailPlayerId(null); }}
      />

      {/* Award Projections */}
      <NFLSeasonAwards teams={sorted} currentLeague={currentLeague} />
        </TabsContent>

        <TabsContent value="bonuses" className="mt-0">
          <BonusesPanel
            leagueId={currentLeague.id}
            teams={teams.map((t) => ({ id: t.id, name: t.name, owner_id: t.owner_id }))}
            currentWeek={currentLeague.current_week ?? 1}
          />
        </TabsContent>

        <TabsContent value="survivor" className="mt-0">
          <SurvivorPanel
            leagueId={currentLeague.id}
            teams={teams.map((t) => ({ id: t.id, name: t.name, owner_id: t.owner_id }))}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  NFL Season Awards                                                    */
/* ------------------------------------------------------------------ */

function NFLSeasonAwards({ teams, currentLeague }: { teams: any[]; currentLeague: any }) {
  // Season awards fetch — own key (season-wide final matchups + active events; NOT the
  // week-scoped shared key, whose filters differ). Cached so returning to Standings shows the
  // computed awards from cache and refetches in the background (no blank). The award list is a
  // pure memo over the fetched data + context `teams`.
  const active = !!currentLeague && teams.length > 0;
  const { data: awardsData, isPending } = useQuery({
    queryKey: ['nfl-awards-data', currentLeague?.id],
    enabled: active,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const [sabRes, matchRes] = await Promise.all([
        supabase
          .from('nfl_sabotage_events' as any)
          .select('attacker_team_id, target_team_id, impact_points')
          .eq('league_id', currentLeague!.id)
          .eq('status', 'active'),
        supabase
          .from('nfl_matchups')
          .select('home_team_id, away_team_id, home_score, away_score, status, week')
          .eq('league_id', currentLeague!.id)
          .in('status', ['final', 'completed']),
      ]);
      return { sabotages: (sabRes.data as any[]) || [], matchups: matchRes.data || [] };
    },
  });

  const awards = useMemo<AwardData[]>(() => {
    if (!awardsData || !active) return [];
    const sabotages = awardsData.sabotages;
    const matchups = awardsData.matchups;
    const teamMap = new Map(teams.map((t: any) => [t.id, t.name]));
    const tn = (id: string) => teamMap.get(id) || 'Unknown';

    const computed: AwardData[] = [];

      // 1. Season MVP — most total points
      const byPF = [...teams].sort((a, b) => b.points_for - a.points_for);
      if (byPF.length > 0) {
        computed.push({
          id: 'mvp',
          icon: <Crown className="w-5 h-5" />,
          label: 'Season MVP',
          teamName: byPF[0].name,
          value: `${Math.round(byPF[0].points_for)} pts`,
          color: 'text-primary',
          description: 'Most total points scored',
        });
      }

      // 2. Best Record
      if (teams.length > 0) {
        computed.push({
          id: 'best-record',
          icon: <Trophy className="w-5 h-5" />,
          label: 'Best Record',
          teamName: teams[0].name,
          value: `${teams[0].wins}-${teams[0].losses}`,
          color: 'text-primary',
          description: 'Best win-loss record in the league',
        });
      }

      // 3. Basement Dweller
      if (teams.length > 0) {
        const worst = teams[teams.length - 1];
        computed.push({
          id: 'basement',
          icon: <Skull className="w-5 h-5" />,
          label: 'Basement Dweller',
          teamName: worst.name,
          value: `${worst.wins}-${worst.losses}`,
          color: 'text-destructive',
          description: 'Worst record in the league',
        });
      }

      // 4. Champion — winner of latest playoff matchup
      if (matchups.length > 0) {
        const sorted = [...matchups].sort((a, b) => b.week - a.week);
        const final = sorted[0];
        const champId = Number(final.home_score) > Number(final.away_score) ? final.home_team_id : final.away_team_id;
        computed.push({
          id: 'champion',
          icon: <Trophy className="w-5 h-5" />,
          label: 'Champion',
          teamName: tn(champId),
          value: 'Week ' + final.week,
          color: 'text-amber-400',
          description: 'League championship winner',
        });
      }

      // 5. Chaos Agent — most sabotages used
      const sabCountByTeam: Record<string, number> = {};
      for (const s of sabotages) {
        sabCountByTeam[s.attacker_team_id] = (sabCountByTeam[s.attacker_team_id] || 0) + 1;
      }
      const topSaboteur = Object.entries(sabCountByTeam).sort((a, b) => b[1] - a[1])[0];
      if (topSaboteur) {
        computed.push({
          id: 'chaos-agent',
          icon: <Bomb className="w-5 h-5" />,
          label: 'Chaos Agent',
          teamName: tn(topSaboteur[0]),
          value: `${topSaboteur[1]} sabotages`,
          color: 'text-primary',
          description: 'Most sabotages deployed',
        });
      }

      // 6. Best Single Week
      const weeklyScores: { team_id: string; score: number; week: number }[] = [];
      for (const m of matchups) {
        weeklyScores.push({ team_id: m.home_team_id, score: Number(m.home_score), week: m.week });
        weeklyScores.push({ team_id: m.away_team_id, score: Number(m.away_score), week: m.week });
      }
      const topWeek = weeklyScores.sort((a, b) => b.score - a.score)[0];
      if (topWeek) {
        computed.push({
          id: 'best-week',
          icon: <Flame className="w-5 h-5" />,
          label: 'Best Single Week',
          teamName: tn(topWeek.team_id),
          value: `${topWeek.score.toFixed(1)} pts (Wk ${topWeek.week})`,
          color: 'text-primary',
          description: 'Highest single-week score',
        });
      }

      // 7. Biggest Blowout
      const blowouts = matchups.map(m => ({
        winner: Number(m.home_score) > Number(m.away_score) ? m.home_team_id : m.away_team_id,
        margin: Math.abs(Number(m.home_score) - Number(m.away_score)),
        week: m.week,
      })).sort((a, b) => b.margin - a.margin);
      if (blowouts.length > 0) {
        computed.push({
          id: 'blowout',
          icon: <Zap className="w-5 h-5" />,
          label: 'Biggest Blowout',
          teamName: tn(blowouts[0].winner),
          value: `+${blowouts[0].margin.toFixed(1)} pts (Wk ${blowouts[0].week})`,
          color: 'text-primary',
          description: 'Largest margin of victory',
        });
      }

      // 8. Longest Win Streak
      const teamResults: Record<string, ('W' | 'L')[]> = {};
      const sortedMatchups = [...matchups].sort((a, b) => a.week - b.week);
      for (const m of sortedMatchups) {
        const hs = Number(m.home_score);
        const as_ = Number(m.away_score);
        if (!teamResults[m.home_team_id]) teamResults[m.home_team_id] = [];
        if (!teamResults[m.away_team_id]) teamResults[m.away_team_id] = [];
        if (hs > as_) {
          teamResults[m.home_team_id].push('W');
          teamResults[m.away_team_id].push('L');
        } else {
          teamResults[m.away_team_id].push('W');
          teamResults[m.home_team_id].push('L');
        }
      }

      let longestStreak = { teamId: '', count: 0 };
      for (const [tid, results] of Object.entries(teamResults)) {
        let maxStreak = 0;
        let cur = 0;
        for (const r of results) {
          if (r === 'W') { cur++; maxStreak = Math.max(maxStreak, cur); }
          else cur = 0;
        }
        if (maxStreak > longestStreak.count) {
          longestStreak = { teamId: tid, count: maxStreak };
        }
      }
      if (longestStreak.count > 0) {
        computed.push({
          id: 'win-streak',
          icon: <TrendingUp className="w-5 h-5" />,
          label: 'Longest Win Streak',
          teamName: tn(longestStreak.teamId),
          value: `${longestStreak.count}W`,
          color: 'text-primary',
          description: 'Longest consecutive win streak',
        });
      }

    return computed;
  }, [awardsData, active, teams, currentLeague]);

  if (active && isPending) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-28 bg-muted/20 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (awards.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold font-display text-foreground flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary" />
          Award Projections
        </h2>
        <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">
          PROJECTED
        </Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {awards.map(award => (
          <Card key={award.id} className="bg-card border-border overflow-hidden transition-all hover:border-primary/30">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className={cn('shrink-0', award.color)}>
                  {award.icon}
                </div>
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide leading-tight">
                  {award.label}
                </span>
              </div>
              <p className="text-sm font-bold text-foreground truncate">{award.teamName}</p>
              <p className={cn('text-xs font-semibold mt-0.5', award.color)}>{award.value}</p>
              <p className="text-[10px] text-muted-foreground mt-1 leading-snug line-clamp-2">{award.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
